import React from 'react';
import PortfolioSummaryVM from './PortfolioSummaryVM';
import { observer } from 'mobx-react';
import { NumberUtils, Misc } from '@singularsystems/neo-core';
import { Neo } from '@singularsystems/neo-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import FutureAmounts from "../../../Models/FutureAmounts";
import { StyleUtils } from 'st-app/Services/StyleUtils';

interface IPortfolioSummaryProps {
    viewModel: PortfolioSummaryVM;
}

@observer
export default class PortfolioSummary extends React.Component<IPortfolioSummaryProps> {

    constructor(props: IPortfolioSummaryProps) {
        super(props);
    }

    public render() {
        const viewModel = this.props.viewModel,
            valuesByYear = viewModel.valuesByYear;

        if (!valuesByYear || !viewModel.portfolioData) {
            return null;
        }

        const taxLabel = viewModel.showProfitAfterTax ? " (after tax):" : ":";
        const portfolioLabel = viewModel.includeAllAwards ? "portfolio" : "selected";

        return (
            <div className="portfolio-view-summary">
                <div>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={this.getChartOptions(valuesByYear)} />
                </div>
                <div className="summary-right">
                    <div className="calculate-options summary-panel">
                        <div className="calculate-options-header">
                            Do you want to:
                        </div>

                        <div style={{ display: "block" }}>
                            <Neo.FormGroup
                                bind={viewModel.portfolioData.meta.instrumentEditMode}
                                label="Edit Prices?"
                                data-tip="Change the prices in the table below to see the effect on profits."
                                data-tip-align="start"
                            />

                            <Neo.FormGroup
                                bind={viewModel.meta.payoutRatioTrackingUnits}
                                label="Apply Payout ratio?"
                                data-tip="The Payout ratio tracking % will be applied to the Total Units column."
                                data-tip-align="start"
                            />

                            <div style={{
                                display: "flex",
                                alignItems: "stretch",
                                justifyContent: "space-between",
                            }}>
                                <div>
                                    <Neo.FormGroup
                                        label="Show Profit After Tax?"
                                        bind={viewModel.meta.showProfitAfterTax}
                                    />
                                </div>

                                <div>
                                    {viewModel.showProfitAfterTax && (
                                        <Neo.FormGroup
                                            className="mb-0"
                                            label="Custom Tax Rate"
                                            bind={viewModel.meta.taxRate}
                                            numProps={{ format: Misc.NumberFormat.PercentNoDecimals }}
                                            style={{ width: 120 }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {!viewModel.includeAllAwards &&
                            <Neo.FormGroup bind={viewModel.meta.includeAllAwards} label="Reset Selection" />}

                    </div>
                    <div className="summary-values summary-panel highlighted-panel">
                        <div className="inline-value">
                            Vested {portfolioLabel} value{taxLabel}<strong>{NumberUtils.format(valuesByYear.vestedValue, Misc.NumberFormat.CurrencyNoDecimals, valuesByYear.currency.symbol)}</strong>
                        </div>
                        <div className="inline-value">
                            <div>
                                Total {portfolioLabel} value {viewModel.payoutRatioTrackingUnits && <span>(<i className="fa fa-arrow-trend-up payout-ratio-icon scale-08" data-tip="Payout ratio % is enabled for this award"/>)</span>}{taxLabel}
                            </div>
                            <strong>{NumberUtils.format(valuesByYear.allTimeValue, Misc.NumberFormat.CurrencyNoDecimals, valuesByYear.currency.symbol)}</strong>
                        </div>
                    </div>

                    {valuesByYear.hasDifferences &&
                        <div className="summary-values summary-panel">
                            <div className="inline-value">
                                Projected value{taxLabel}<strong>{NumberUtils.format(valuesByYear.allTimeValue2, Misc.NumberFormat.CurrencyNoDecimals, valuesByYear.currency.symbol)}</strong>
                            </div>
                        </div>}
                </div>
            </div>
        );
    }

    public getChartOptions(data: FutureAmounts) {
        let width = 700;
        if (window.innerWidth < 992) {
            width = window.innerWidth / 2;
        } else if (window.innerWidth < 1200) {
            width = 500;
        } else if (window.innerWidth < 1700) {
            width = 600;
        }

        const colors = StyleUtils.getGraphColors();
        const lightenColor = (color: string) => Highcharts.color(color).setOpacity(0.6).get('rgba') as string;
        const hasCostsAlreadyPaidGroups = this.props.viewModel.portfolioService.appData?.hasCostsAlreadyPaidGroups ?? false;
        const metricLabel = hasCostsAlreadyPaidGroups ? "value" : "profit";
        const currentWording = hasCostsAlreadyPaidGroups ? "Current potential value" : "Vested potential profit";

        const showGrossValueAtAwardDate = this.props.viewModel.portfolioService.appData?.settings.showGrossValueAtAwardDate ?? false;

        const series: Highcharts.SeriesColumnOptions[] = [];

        const isComparison = data.hasDifferences;

        const dataLabels = {
            enabled: true,
            format: data.currency.symbol + "{y:,.0f}",
            style: { fontSize: "11px", color: "inherit" }
        };

        if (isComparison) {
            series.push({
                type: "column",
                name: "Projected",
                id: "projected",
                data: [data.vestedValue2, data.yearPlus1Value2, data.yearPlus2Value2, data.yearPlus3Value2, data.allTimeValue2],
                color: '#d6d8dA',
                colorByPoint: false,
                dataLabels: dataLabels,
                pointPlacement: 0.1,
            });
        }

        series.push({
            type: "column",
            name: "Current",
            data: [data.vestedValue, data.yearPlus1Value, data.yearPlus2Value, data.yearPlus3Value, data.allTimeValue],
            colors: [colors[0], colors[1], colors[1], colors[1], colors[2]].map(lightenColor),
            colorByPoint: true,
            linkedTo: "projected",
            dataLabels: isComparison ? { enabled: false } : dataLabels,
            pointPlacement: isComparison ? -0.1 : 0,
        });

        if (showGrossValueAtAwardDate) {
            series.push({
                type: "column",
                name: "Gross Value at Award Date",
                data: [
                    {
                        y: data.grossValueAtAwardDateVestedValue,
                        custom: { growth: data.vestedGrowthPercentage }
                    },
                    {
                        y: data.grossValueAtAwardDateYearPlus1Value,
                        custom: { growth: data.yearPlus1GrowthPercentage }
                    },
                    {
                        y: data.grossValueAtAwardDateYearPlus2Value,
                        custom: { growth: data.yearPlus2GrowthPercentage }
                    },
                    {
                        y: data.grossValueAtAwardDateYearPlus3Value,
                        custom: { growth: data.yearPlus3GrowthPercentage }
                    },
                    {
                        y: data.grossValueAtAwardDateAllTimeValue,
                        custom: { growth: data.allTimeGrowthPercentage }
                    }
                ],
                colors: [colors[0], colors[1], colors[1], colors[1], colors[2]],
                colorByPoint: true,
                pointPlacement: isComparison ? -0.3 : -0.2,
                zIndex: 2,
            });
        }

        return {
            chart: {
                type: "column",
                height: 300,
                width: width,
                backgroundColor: "transparent",
            },
            title: { text: "" },
            xAxis: {
                type: "category",
                categories: this.props.viewModel.summaryCategoryLabels.map(c => c.label)
            },
            yAxis: { visible: false, max: Math.max(data.allTimeValue, data.allTimeValue2) * 1.1 },
            legend: { enabled: false },
            series: series,
            plotOptions: {
                series: {
                    grouping: false,
                    borderWidth: 0
                },
            },
            tooltip: {
                shared: true,
                useHTML: true,
                formatter: function () {

                    const category = this.points?.[0]?.category ?? "";

                    let html = `<span style="font-size:10px">${category}</span><br/>`;

                    const points = this.points ?? [];

                    const projected = points.find(p => p.series.name === "Projected");
                    const current = points.find(p => p.series.name === "Current");
                    const gross = points.find(p => p.series.name === "Gross Value at Award Date") as any;

                    if (projected) {
                        html += `
                            <span style="color:${projected.color}">●</span>
                            Projected:
                            <b>${data.currency.symbol}${Highcharts.numberFormat(projected.y as number, 0)}</b>
                            <br/>
                        `;
                    }

                    if (current) {
                        html += `
                            <span style="color:${current.color}">●</span>
                            Current:
                            <b>${data.currency.symbol}${Highcharts.numberFormat(current.y as number, 0)}</b>
                            <br/>
                        `;
                    }

                    if (gross) {
                        html += `
                            <span style="color:${gross.color}">●</span>
                            Gross Value at Award Date:
                            <b>${data.currency.symbol}${Highcharts.numberFormat(gross.y as number, 0)}</b>
                            <br/>
                            <span style="padding-left: 18px;">
                                Portfolio Growth:
                                <b>${(gross.point.custom?.growth ?? 0).toFixed(2)}%</b>
                            </span>
                        `;
                    }

                    return html;
                }
            },
            credits: { enabled: false }
        } as Highcharts.Options;
    }
}